import { type CSSProperties, type ReactNode } from 'react';

import { type Repub } from '@cx-modules/constants';
import { type Country, type StateCode } from '@cx-modules/types';

import { getContentForId } from '~/api/content-delivery-api/content-delivery-api.service';
import { loadOffersListForServer } from '~/modules/OffersList/OffersList.server';
import { type Loggable } from '~/traits/Loggable';
import { type GetTemplateContextArgs } from '~/types/template';
import { getEnv } from '~/utils/env.server';
import { getDeviceFromUserAgent } from '~/utils/getDeviceFromUserAgent';
import { getRepubFromURL } from '~/utils/getRepubFromURL';

type OffersPageTemplateArgs = GetTemplateContextArgs & {
	countryCode: Country;
	deviceTypeForApi?: string;
	path: string;
	subdivisionCode?: StateCode;
};

export async function getTemplateContext(
	this: Loggable,
	{
		countryCode,
		deviceTypeForApi,
		path,
		request,
		subdivisionCode,
	}: OffersPageTemplateArgs,
) {
	const url = new URL(request.url);
	const userAgent = request.headers.get('User-Agent');

	const deviceType = getDeviceFromUserAgent(userAgent);
	const repub =
		(url.searchParams.get('repub') as Repub) || getRepubFromURL(url);
	const ENV = getEnv();

	const apiKey = url.searchParams.get('apiKey') ?? ENV.OC_API_KEY[repub];
	const offersListResponse = await loadOffersListForServer({
		apiKey,
		path,
		repub,
		deviceTypeForApi,
		...(countryCode ? { countryCode } : {}),
		...(subdivisionCode ? { subdivisionCode } : {}),
	});

	const queryDehydratedState = offersListResponse.dehydratedState.queries[0];
	if (!queryDehydratedState) {
		this.logger.error('No dehydrated state for OffersList found');
		throw new Response(null, {
			status: 500,
			statusText: 'No dehydrated state for OffersList found',
		});
	}
	const { evergreenContent } = queryDehydratedState?.state?.data as {
		evergreenContent?: { id: string };
	};

	let evergreenHtml;
	try {
		evergreenHtml =
			(
				evergreenContent &&
				(await getContentForId.call(this, evergreenContent.id))
			)?.body ?? '';
	} catch (error) {
		let exception;
		if (error instanceof Error) {
			exception = error;
		} else {
			const err = error as any;
			exception = err.error || err.message || err.originalError || err;
		}
		this.logger.error(exception);
	}
	this.logger.info(
		`[offers-page/template.tsx]: Evergreen content ${evergreenHtml ? 'found' : 'not found'}`,
	);

	return {
		apiKey,
		countryCode,
		deviceType,
		evergreenHtml,
		offersListResponse,
		path,
		repub,
		subdivisionCode,
	};
}

export default function OffersPageTemplate({
	className,
	evergreenHtml,
	offersList,
	style,
}: {
	className?: string;
	evergreenHtml?: string;
	offersList: ReactNode;
	style?: CSSProperties;
}) {
	return (
		<div className={className} style={style}>
			{offersList}
			{evergreenHtml && (
				<section
					className="evergreen"
					dangerouslySetInnerHTML={{ __html: evergreenHtml }}
				/>
			)}
		</div>
	);
}
